import React, { useEffect, useState } from 'react'
import adminApi from './api'
import { ConfirmModal } from './components/ConfirmModal'
import { Notification } from './components/Notification'

interface Company {
  id: number
  name: string
  logo_url: string | null
  website: string | null
  instagram_url?: string | null
  email?: string | null
  description?: { ru?: string; ky?: string; en?: string } | string | null
  display_order: number
  created_at: string
  updated_at: string
}

export const CompaniesAdmin: React.FC = () => {
  const [companies, setCompanies] = useState<Company[]>([])

  const [loading, setLoading] = useState(true)
  const [modalOpen, setModalOpen] = useState(false)
  const [editingCompany, setEditingCompany] = useState<Company | null>(null)
  const [openMenuId, setOpenMenuId] = useState<number | null>(null)
  const [userRole, setUserRole] = useState<string>('')
  const [confirmDelete, setConfirmDelete] = useState<{ show: boolean; id: number | null }>({ show: false, id: null })
  const [notification, setNotification] = useState<{ message: string; type: 'success' | 'error' } | null>(null)

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    website: '',
    instagramUrl: '',
    email: '',
    descriptionRu: '',
    descriptionKy: '',
    descriptionEn: ''
  })
  const [logoFile, setLogoFile] = useState<File | null>(null)
  const [logoPreview, setLogoPreview] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [formErrors, setFormErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    const userData = localStorage.getItem('admin_user')
    if (userData) {
      const user = JSON.parse(userData)
      setUserRole(user.role)
    }
    fetchCompanies()
  }, [])

  const fetchCompanies = async () => {
    try {
      setLoading(true)
      const { data } = await adminApi.get('/api/admin/companies')
      setCompanies(data.companies || [])
    } catch (err) {
      console.error('Error fetching companies', err)
    } finally {
      setLoading(false)
    }
  }

  const openModal = (company?: Company) => {
    if (company) {
      setEditingCompany(company)
      const desc = company.description
      const descObj = typeof desc === 'string' ? { ru: desc, ky: '', en: '' } : (desc || {})
      setFormData({
        name: company.name,
        website: company.website || '',
        instagramUrl: company.instagram_url || '',
        email: company.email || '',
        descriptionRu: descObj.ru || '',
        descriptionKy: descObj.ky || '',
        descriptionEn: descObj.en || ''
      })
      setLogoPreview(company.logo_url || null)
    } else {
      setEditingCompany(null)
      setFormData({ name: '', website: '', instagramUrl: '', email: '', descriptionRu: '', descriptionKy: '', descriptionEn: '' })
      setLogoPreview(null)
    }
    setLogoFile(null)
    setModalOpen(true)
  }

  const closeModal = () => {
    setModalOpen(false)
    setEditingCompany(null)
    setFormData({ name: '', website: '', instagramUrl: '', email: '', descriptionRu: '', descriptionKy: '', descriptionEn: '' })
    setLogoFile(null)
    setLogoPreview(null)
    setFormErrors({})
  }

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setLogoFile(file)
      const reader = new FileReader()
      reader.onloadend = () => setLogoPreview(reader.result as string)
      reader.readAsDataURL(file)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitting(true)

    const errors: Record<string, string> = {}
    if (!formData.name.trim()) errors.name = 'Обязательное поле'
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors)
      setSubmitting(false)
      return
    }

    try {
      const formDataToSend = new FormData()
      formDataToSend.append('name', formData.name)
      formDataToSend.append('website', formData.website)
      formDataToSend.append('instagramUrl', formData.instagramUrl)
      formDataToSend.append('email', formData.email)
      formDataToSend.append(
        'description',
        JSON.stringify({
          ru: formData.descriptionRu || '',
          ky: formData.descriptionKy || '',
          en: formData.descriptionEn || ''
        })
      )
      if (logoFile) formDataToSend.append('logo', logoFile)

      if (editingCompany) {
        await adminApi.put(`/api/admin/companies/${editingCompany.id}`, formDataToSend)
      } else {
        await adminApi.post('/api/admin/companies', formDataToSend)
      }

      closeModal()
      fetchCompanies()
      setNotification({ message: 'Логотип сохранён', type: 'success' })
    } catch (err) {
      setNotification({ message: 'Ошибка при сохранении', type: 'error' })
    } finally {
      setSubmitting(false)
    }
  }

  const handleDelete = (id: number) => {
    setConfirmDelete({ show: true, id })
    setOpenMenuId(null)
  }

  const handleConfirmDelete = async () => {
    if (!confirmDelete.id) return
    try {
      await adminApi.delete(`/api/admin/companies/${confirmDelete.id}`)
      fetchCompanies()
      setNotification({ message: 'Партнёр удалён', type: 'success' })
    } catch (err) {
      setNotification({ message: 'Ошибка при удалении', type: 'error' })
    } finally {
      setConfirmDelete({ show: false, id: null })
    }
  }

  return (
    <div>
      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}

      <div className="page-header flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="page-title">Партнёры</h1>
          <p className="page-subtitle">Справочник партнёрских компаний</p>
        </div>
        <button className="btn-primary" onClick={() => openModal()}>
          Добавить партнёра
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center items-center py-20">
          <div className="loading-spinner w-10 h-10"></div>
        </div>
      ) : companies.length === 0 ? (
        <div className="text-center px-5 py-[60px] bg-[#fafafa] rounded-lg border border-dashed border-[#d9d9d9] text-sm text-black/45">
          Пока нет партнёров. Нажмите «Добавить партнёра», чтобы создать первого.
        </div>
      ) : (
        <div
          className="grid gap-2"
          // dynamic: auto-fill grid without Tailwind's fixed cols API
          style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))' }}
        >
          {companies.map(company => {
            return (
              <div
                key={company.id}
                className="relative flex flex-col items-center justify-between px-1.5 pt-2 pb-1.5 rounded-md gap-1 min-h-[90px] border border-gray-200 bg-white"
              >
                {/* Menu button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    setOpenMenuId(openMenuId === company.id ? null : company.id)
                  }}
                  className="absolute top-0 right-0 bg-transparent border-0 cursor-pointer px-1.5 py-0.5 text-sm text-gray-500 leading-none"
                >
                  ⋮
                </button>
                {openMenuId === company.id && (
                  <>
                    <div
                      className="fixed inset-0 z-[999]"
                      onClick={(e) => { e.stopPropagation(); setOpenMenuId(null) }}
                    />
                    <div
                      onClick={(e) => e.stopPropagation()}
                      className="absolute right-1 top-8 bg-white border border-[#e0e0e0] rounded shadow-md z-[1000] min-w-[150px]"
                    >
                      <button
                        onClick={() => { setOpenMenuId(null); openModal(company) }}
                        className="block w-full px-4 py-2.5 border-0 bg-transparent text-left cursor-pointer text-sm text-[#333] hover:bg-[#f5f5f5]"
                      >
                        Изменить
                      </button>
                      {userRole === 'admin' && (
                        <button
                          onClick={() => handleDelete(company.id)}
                          className="block w-full px-4 py-2.5 border-0 bg-transparent text-left cursor-pointer text-sm text-[#ff4d4f] hover:bg-[#fff2f0]"
                        >
                          Удалить
                        </button>
                      )}
                    </div>
                  </>
                )}

                <div className="w-full h-12 flex items-center justify-center mt-1">
                  {company.logo_url ? (
                    <img
                      src={company.logo_url}
                      alt={company.name}
                      className="max-w-full max-h-12 object-contain"
                    />
                  ) : (
                    <div className="text-[10px] text-black/35">Без лого</div>
                  )}
                </div>
                <div className="text-[11px] text-black/75 text-center w-full overflow-hidden text-ellipsis whitespace-nowrap">
                  {company.name}
                </div>
              </div>
            )
          })}
        </div>
      )}

      {modalOpen && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">
                {editingCompany ? 'Редактировать логотип' : 'Добавить логотип'}
              </h2>
              <button className="modal-close" onClick={closeModal}>&times;</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="modal-field">
                  <label htmlFor="companyName" className="modal-label">Название *</label>
                  <input
                    id="companyName"
                    type="text"
                    name="companyName"
                    className={formErrors.name ? 'form-control border-red-500' : 'form-control'}
                    value={formData.name}
                    onChange={(e) => {
                      setFormData({ ...formData, name: e.target.value })
                      if (formErrors.name) setFormErrors({ ...formErrors, name: '' })
                    }}
                    autoComplete="off"
                  />
                  {formErrors.name && <div className="field-error">{formErrors.name}</div>}
                </div>

                <div className="modal-field">
                  <label htmlFor="logo-upload" className="modal-label">Логотип</label>
                  {logoPreview && (
                    <div className="mb-3">
                      <img
                        src={logoPreview}
                        alt="Preview"
                        className="max-w-[200px] max-h-[200px] object-contain border border-[#e0e0e0] rounded p-2"
                      />
                    </div>
                  )}
                  <label htmlFor="logo-upload" className="btn-secondary cursor-pointer inline-block">
                    Загрузить логотип
                  </label>
                  <input
                    id="logo-upload"
                    name="companyLogo"
                    type="file"
                    accept="image/png,image/svg+xml"
                    onChange={handleLogoChange}
                    autoComplete="off"
                    className="hidden"
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    PNG или SVG, до 2 МБ. Сервер автоматически конвертирует в WebP для сайта.
                  </p>
                </div>

                <div className="modal-field">
                  <label htmlFor="companyWebsite" className="modal-label">Сайт</label>
                  <input
                    id="companyWebsite"
                    type="url"
                    name="companyWebsite"
                    className="form-control"
                    value={formData.website}
                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                    placeholder="https://example.com"
                    autoComplete="url"
                  />
                </div>

                <div className="modal-field">
                  <label htmlFor="companyInstagram" className="modal-label">Instagram</label>
                  <input
                    id="companyInstagram"
                    type="url"
                    name="companyInstagram"
                    className="form-control"
                    value={formData.instagramUrl}
                    onChange={(e) => setFormData({ ...formData, instagramUrl: e.target.value })}
                    placeholder="https://instagram.com/handle"
                    autoComplete="off"
                  />
                </div>

                <div className="modal-field">
                  <label htmlFor="companyEmail" className="modal-label">Email (необязательно)</label>
                  <input
                    id="companyEmail"
                    type="email"
                    name="companyEmail"
                    className="form-control"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="partner@company.kg"
                    autoComplete="email"
                  />
                </div>

                <div className="modal-field">
                  <label className="modal-label">Описание (ру)</label>
                  <textarea
                    className="form-control"
                    rows={2}
                    value={formData.descriptionRu}
                    onChange={(e) => setFormData({ ...formData, descriptionRu: e.target.value })}
                    placeholder="Короткое описание компании"
                  />
                </div>
                <div className="modal-field">
                  <label className="modal-label">Описание (ky)</label>
                  <textarea
                    className="form-control"
                    rows={2}
                    value={formData.descriptionKy}
                    onChange={(e) => setFormData({ ...formData, descriptionKy: e.target.value })}
                  />
                </div>
                <div className="modal-field">
                  <label className="modal-label">Описание (en)</label>
                  <textarea
                    className="form-control"
                    rows={2}
                    value={formData.descriptionEn}
                    onChange={(e) => setFormData({ ...formData, descriptionEn: e.target.value })}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={closeModal}>
                  Отмена
                </button>
                <button type="submit" className="btn-primary" disabled={submitting}>
                  {submitting ? 'Сохранение...' : 'Сохранить'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={confirmDelete.show}
        title="Подтвердите удаление"
        message="Вы уверены, что хотите удалить этот логотип? Он будет удалён из всех событий, к которым был привязан."
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmDelete({ show: false, id: null })}
        confirmText="Удалить"
        cancelText="Отмена"
        isDanger={true}
      />
    </div>
  )
}
